import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { SessionPriceChips, Button, SubmitButton } from 'components';
import { Trans } from 'react-i18next';
import { Session, SessionStatus } from '@app/api';
import { usePrevStepPath, useNextStepPath } from '../hooks/useSessionSteps';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  line: {
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
    zIndex: 1,

    display: 'flex',
    alignItems: 'center',
    padding: [[theme.marginBase * 2, theme.marginBase * 4]],
    paddingRight: theme.marginBase * 2,
  },
  button: {
    marginRight: theme.marginBase * 2,
  },
  buttonIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  prices: {
    flex: 1,
  },
}));

interface Props {
  session: Session;

  duration?: number;
  numberOfParticipantRequired?: number;
  countryKeys?: string[];
  hidePrices?: boolean;

  saving?: boolean;
  publish?: boolean;

  showPreviewButton?: boolean;
  onSubmit?: () => any;
  noSubmit?: boolean;
}

export const SessionSubmitLine = ({
  session,
  numberOfParticipantRequired,
  countryKeys,
  duration,
  hidePrices,
  saving,
  showPreviewButton,
  publish,
  onSubmit,
  noSubmit,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const published = session.status !== SessionStatus.DRAFT;

  const prevPath = usePrevStepPath(session);
  const nextPath = useNextStepPath(session);

  return (
    <div className={classes.line}>
      {!!prevPath && !published && (
        <Button
          className={classes.button}
          small
          variant="transparent"
          to={prevPath}
          withBackArrow
        >
          <Trans i18nKey="editSession.buttons.back" />
        </Button>
      )}
      <div className={classes.prices}>
        {session.planId && !hidePrices && (
          <SessionPriceChips
            numberOfParticipantRequired={numberOfParticipantRequired}
            duration={duration}
            session={session}
            countryKeys={countryKeys}
          />
        )}
      </div>

      {!noSubmit && (
        <>
          {published ? (
            <SubmitButton
              className={classes.button}
              small
              loading={saving}
              onClick={onSubmit}
              color="accent"
            >
              <Trans i18nKey="editSession.buttons.save" />
            </SubmitButton>
          ) : (
            <>
              {showPreviewButton && (
                <Button
                  to={`/sessions/preview/${session.id}`}
                  className={classes.button}
                  small
                  color="blue"
                  variant="transparent"
                >
                  <i
                    className={classnames('ri-eye-line', classes.buttonIcon)}
                  />
                  <Trans i18nKey="editSession.buttons.preview" />
                </Button>
              )}
              {!!nextPath && (
                <Button
                  className={classes.button}
                  small
                  variant="transparent"
                  to={nextPath}
                >
                  <Trans i18nKey="editSession.buttons.skip" />
                </Button>
              )}
              <SubmitButton
                className={classes.button}
                small
                withNextArrow={!publish}
                loading={saving}
                color={publish ? 'green' : 'accent'}
                onClick={onSubmit}
              >
                <Trans
                  i18nKey={
                    publish
                      ? 'editSession.buttons.publish'
                      : 'editSession.buttons.submit'
                  }
                />
              </SubmitButton>
            </>
          )}
        </>
      )}
    </div>
  );
};
